// Standings VIEW model — the column/legend/formatting logic that sits on top
// of the shared ordering engine (`createStandings`, ADR 0029).
//
// Shared because TWO surfaces render the same table and must agree on what a
// column is, which tiebreakers are visible, how a value reads and what the
// legend says: the public division Standings tab (apps/app + apps/next-app)
// and the admin division page's Standings view (INT-0055, ADR 0071). Each
// consumer keeps its OWN markup and design system; nothing in here renders.
//
// Behavior preserved verbatim from the public Standings.tsx (2026-10-30):
//  - `config.tiebreakers[sport]` drives the columns; entries may be legacy
//    strings or `{ id, hidden?, maxPointDifferential? }` objects.
//  - A `hidden` tiebreaker leaves the columns and legend but STILL sorts
//    (createStandings reads the full chain). Hiding every one falls back to
//    the default PD/PA columns, same as an unconfigured chain.
//  - Rows fall back to the bare team list when no game has been scored.
import _ from 'lodash';
import { createStandings } from './standings';

export type TiebreakerEntry =
  | string
  | { id: string; hidden?: boolean; maxPointDifferential?: number | null; [key: string]: unknown };

export interface StandingsColumn {
  /** Row field read for the cell (`pd`, `pa`, or the tiebreaker id). */
  key: string;
  /** Short header: PS, PA, PD, H2H (or the raw id for an unknown tiebreaker). */
  label: string;
  /** Header tooltip / legend text. */
  description: string;
  /** The configured tiebreaker behind the column; null for the default PD/PA columns. */
  tiebreaker: TiebreakerEntry | null;
  /** Cell text for a standings row. */
  format: (row: any) => string | number;
}

export interface StandingsGroup {
  /** Sub-division name, or null for ungrouped rows. */
  subDivision: string | null;
  rows: any[];
}

export interface StandingsLegendItem {
  label: string;
  description: string;
}

export interface StandingsView {
  /** Ordered rows (createStandings output), or the bare team list when nothing is scored. */
  rows: any[];
  /** True when `rows` came from scored games (false = team-list fallback). */
  hasResults: boolean;
  groups: StandingsGroup[];
  columns: StandingsColumn[];
  /** True when the columns are the configured tiebreakers (false = default PD/PA). */
  fromTiebreakers: boolean;
  /** Tiebreaker Order legend, or null when no visible tiebreaker is configured. */
  legend: StandingsLegendItem[] | null;
  /** Show the "playoff ties go to the higher-placed team" note. */
  showPlayoffTieNote: boolean;
}

export function tiebreakerId(tiebreaker: TiebreakerEntry): string {
  return typeof tiebreaker === 'string' ? tiebreaker : tiebreaker.id;
}

/** The sport's configured chain, or null when none is configured. */
export function sportTiebreakers(config: any, sport: string): TiebreakerEntry[] | null {
  return (config && config.tiebreakers && config.tiebreakers[sport]) || null;
}

/** Display-only filter: the TiebreakerModal's "Hide in Standings" switch. */
export function visibleTiebreakers(config: any, sport: string): TiebreakerEntry[] {
  return (sportTiebreakers(config, sport) || []).filter(
    (t) => !(typeof t === 'object' && t.hidden),
  );
}

export function tiebreakerLabel(tiebreaker: TiebreakerEntry): string {
  const id = tiebreakerId(tiebreaker);
  switch (id) {
    case 'pointsScored': return 'PS';
    case 'pointsAllowed': return 'PA';
    case 'pointDifferential': return 'PD';
    case 'headToHead': return 'H2H';
    default: return id;
  }
}

export function tiebreakerDescription(tiebreaker: TiebreakerEntry, config: any = {}): string {
  const id = tiebreakerId(tiebreaker);
  switch (id) {
    case 'pointsScored': return 'Total points scored';
    case 'pointsAllowed': return 'Total points allowed (lower is better)';
    case 'pointDifferential': {
      const maxDiff = config && config.maxPointDifferential;
      const diffText = maxDiff ? ` (max ${maxDiff} per game)` : '';
      return `Point differential${diffText}`;
    }
    case 'headToHead': return 'Head-to-head record against tied teams';
    default: return id;
  }
}

/** Cell text for a tiebreaker column: rounded points, signed H2H differential or W-L. */
export function formatTiebreakerValue(row: any, tiebreaker: TiebreakerEntry): string | number {
  const id = tiebreakerId(tiebreaker);
  const value = row ? row[id] : undefined;
  if (value === undefined || value === null) return '';
  switch (id) {
    case 'pointsScored':
    case 'pointsAllowed':
    case 'pointDifferential':
      return Math.round(value);
    case 'headToHead': {
      if (row.headToHeadWins !== undefined && row.headToHeadLosses !== undefined) {
        return `${row.headToHeadWins}-${row.headToHeadLosses}`;
      }
      if (value > 0) return `+${value}`;
      if (value < 0) return String(value);
      return '0';
    }
    default:
      return value;
  }
}

/** Default PD cell: signed when positive, raw otherwise (public table idiom). */
export function formatPointDifferential(row: any): string | number {
  const pd = row ? row.pd : undefined;
  if (pd === undefined || pd === null) return '';
  return Number(pd) > 0 ? `+${pd}` : pd;
}

export function standingsColumns(config: any, sport: string): { columns: StandingsColumn[]; fromTiebreakers: boolean } {
  const visible = visibleTiebreakers(config, sport);
  if (visible.length > 0) {
    return {
      fromTiebreakers: true,
      columns: visible.map((tiebreaker) => ({
        key: tiebreakerId(tiebreaker),
        label: tiebreakerLabel(tiebreaker),
        description: tiebreakerDescription(tiebreaker, config),
        tiebreaker,
        format: (row: any) => formatTiebreakerValue(row, tiebreaker),
      })),
    };
  }
  return {
    fromTiebreakers: false,
    columns: [
      { key: 'pd', label: 'PD', description: 'Point differential', tiebreaker: null, format: formatPointDifferential },
      { key: 'pa', label: 'PA', description: 'Total points allowed (lower is better)', tiebreaker: null, format: (row: any) => (row && row.pa != null ? row.pa : '') },
    ],
  };
}

/** Group rows by `subDivision`; group order = keys sorted then reversed (public idiom). */
export function groupStandings(rows: any[]): StandingsGroup[] {
  const bySub = _.groupBy(rows || [], 'subDivision');
  return Object.keys(bySub)
    .sort()
    .reverse()
    .map((key) => ({ subDivision: key === 'undefined' ? null : key, rows: bySub[key] }));
}

export function standingsLegend(config: any, sport: string): StandingsLegendItem[] | null {
  const visible = visibleTiebreakers(config, sport);
  if (!visible.length) return null;
  return visible.map((tiebreaker) => ({
    label: tiebreakerLabel(tiebreaker),
    description: tiebreakerDescription(tiebreaker, config),
  }));
}

/**
 * Everything a standings table needs, from the raw inputs both surfaces hold.
 * `games` may include playoff games; they are excluded here (regular season
 * only), so callers that already filtered are unaffected.
 */
export function buildStandingsView({
  games = [], teams = [], config = {}, sport = 'basketball',
}: { games?: any[]; teams?: any[]; config?: any; sport?: string } = {}): StandingsView {
  const regularGames = (games || []).filter((game: any) => game && !game.playoffRound);
  const standings = createStandings(regularGames, config || {}, sport);
  const hasResults = standings.length > 0;
  const rows = hasResults ? standings : (teams || []);
  const { columns, fromTiebreakers } = standingsColumns(config, sport);
  return {
    rows,
    hasResults,
    groups: groupStandings(rows),
    columns,
    fromTiebreakers,
    legend: standingsLegend(config, sport),
    showPlayoffTieNote: !!(config && config.breakPlayoffTiesBySeed),
  };
}

export default buildStandingsView;
